import { friendlyCrmSyncError } from "@/lib/crmSyncError";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, Grip, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/atoms/Button";
import { Skeleton } from "@/components/atoms/Skeleton";
import { Tooltip } from "@/components/atoms/Tooltip";
import { CopyButton } from "@/components/atoms/CopyButton";
import { Drawer } from "@/components/organisms/Drawer";
import { ContactDeleteDialog } from "./ContactDeleteDialog";
import { ContactTransferDialog } from "./ContactTransferDialog";
import { DuplicateConflictBanner } from "./DuplicateConflictBanner";
import type { ContactDeletionImpact, ContactTransfer } from "@/types/contactDeletion";
import { useResource } from "@/hooks/useResource";
import { useCountries } from "@/hooks/useCountries";
import { contactsService } from "@/services/contactsService";
import {Input} from "@/components/atoms/Input";
import {TextArea} from "@/components/atoms/TextArea";
import {toast} from "react-toastify";
import { showActionError, showActionErrorFromUnknown } from "@/lib/feedback";
import { crmDisplayName } from "@/lib/syncAlto";
import { ContactSummary, type Option } from "@/types/entities";
import {
  useForm,
  useFieldArray,
  type Control,
  type FieldErrors,
  type FieldPath,
  type UseFormGetValues,
  type UseFormRegister,
  type UseFormSetValue,
  type UseFormWatch,
} from "react-hook-form";
import { MenuSelect } from "@/components/molecules/MenuSelect";
import { useSettingsStore } from "@/store/settingsStore";
import { useUiStore } from "@/store/uiStore";
import { branchesService } from "@/services/branchesService";
import { usersService } from "@/services/usersService";
import {
  applyContactApiValidationToForm,
  applyContactInlineValidationToForm,
    buildContactCreatePayload,
  buildContactUpdatePatch,
  contactEditFormHasChanges,
  buildDefaultSalutation,
  contactDetailsToSummary,
  emptyPersonStub,
  emptyPhoneStub,
  emptyEmailStub,
  hasAnyEmailAddress,
  hasAnyPhoneNumber,
  firstLiveEmailAddress,
  firstLivePhoneNumber,
  isContactPointDeleted,
  describeContactEditChanges,
  MAX_PHONE_NUMBERS_PER_PERSON,
  MAX_EMAIL_ADDRESSES_PER_PERSON,
  EMAIL_TYPE_SELECT_OPTIONS,
  enrichContactSummary,
  extractDialCode,
  getEmptyContactFormDefaults,
  normalizeContactForForm,
  FORENAME_REQUIRED_MESSAGE,
  SURNAME_REQUIRED_MESSAGE,
  EMAIL_REQUIRED_MESSAGE,
  PHONE_REQUIRED_MESSAGE,
  EMAIL_REQUIRED_FOR_PERSON_MESSAGE,
  PHONE_REQUIRED_FOR_PERSON_MESSAGE,
  COUNTRY_CODE_REGEX,
  COUNTRY_CODE_VALIDATION_MESSAGE,
  CONTACT_MOBILE_PHONE_REGEX,
  CONTACT_MOBILE_PHONE_VALIDATION_MESSAGE,
  PHONE_TYPE_SELECT_OPTIONS,
  phoneSelectOptionsWithValue,
  resolveContactUserAtlasId,
  contactCategorySupportsRequirements,
  validateContactCreateInput,
  isUserRequiredForCrm,
  USER_REQUIRED_MESSAGE,
  validateContactFormInput,
  INTENTION_CODE_OPTIONS,
  intentionNameFromCode,
  LEAD_SOURCE_CODE_OPTIONS,
  leadSourceNameFromCode,
  defaultApplicantRequirement,
  personHasContent,
  resolvePhoneCodeOptionValue,
} from "./contactFormUtils";
import { ApplicantRequirement, BranchAssociations, ContactDetails } from "@/types/contact";
import { FileNotesPanel } from "./FileNotesPanel";
import { ApplicantRequirementSummary } from "./ApplicantRequirementsTab";
import { getApiErrorMessage } from "@/utils/apiErrors";
import { cn } from "@/lib/cn";
import {
  EMPTY_CONTACT_INLINE_ERRORS,
  hasContactInlineValidationErrors,
  isValidEmailForUniquenessCheck,
  isValidPhoneForUniquenessCheck,
} from "./contactUniqueness";
import { useContactFieldUniqueness } from "./useContactFieldUniqueness";
import { AddressPreview } from "@/components/molecules/AddressPreview";

interface Props {
  open: boolean;
  contactId: string | null;
  onClose: () => void;
  onSaved?: () => void;
  onUpdate: (res: ContactSummary) => void;
  onCreate?: (res: ContactSummary) => void;
  /** Called after this contact has been permanently deleted, with the impact report. */
  onDeleted?: (impact: ContactDeletionImpact) => void;
  onDeleteScheduled?: (contactIds: string[]) => void;
  onDeleteUndo?: (contactIds: string[]) => void;
}
const CONTACT_TYPE_OPTIONS = [
  { value: "UNKNOWN", label: "UNKNOWN" },
  { value: "CLIENT", label: "CLIENT" },
  { value: "CONTACTRELATIONSHIP", label: "CONTACT RELATIONSHIP" },
];

const SALUTATION_OPTIONS = [
    { value: "MR", label: "Mr." },
    { value: "MRS", label: "Mrs." },
    { value: "MS", label: "Ms." },
    { value: "MISS", label: "Miss" },
    { value: "OTHER", label: "Other" },
];

const INTENTION_OPTIONS = INTENTION_CODE_OPTIONS.map((o) => ({ value: o.code, label: o.name }));
const LEAD_SOURCE_OPTIONS = LEAD_SOURCE_CODE_OPTIONS.map((o) => ({ value: o.code, label: o.name }));
const TABS = ["Contact", "Timeline", "Properties", "Documents", "Requirements", "Transactions"];
const SUBTABS = ["Contact", "Position", "Preference Centre", "Other Addresses", "Bank Details", "Compliance"];
const REQUIREMENT_INTENTION_OPTIONS = [
  { value: "Rent", label: "Rent" },
  { value: "Buy", label: "Buy" },
];
const REQUIREMENT_NEWBUILD_OPTIONS = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];
const REQUIREMENT_QUALIFIER_OPTIONS = [
  { value: "MONTHLY", label: "Monthly" },
  { value: "WEEKLY", label: "Weekly" },
];

function isBuyApplicantIntention(intention: string | null | undefined): boolean {
  return (intention ?? "").trim().toLowerCase() === "buy";
}

function isRentApplicantIntention(intention: string | null | undefined): boolean {
  return (intention ?? "").trim().toLowerCase() === "rent";
}

function hasApplicantRequirementIntention(intention: string | null | undefined): boolean {
  return isBuyApplicantIntention(intention) || isRentApplicantIntention(intention);
}

type ApplicantRequirementFormField =
  | "profileIntention"
  | "rentalIntentionFurnishing"
  | "rentalIntentionPriceQualifier"
  | "priceFrom"
  | "priceTo"
  | "bedsMin"
  | "isNewBuild";

function applicantRequirementFieldPath(
  index: number,
  field: ApplicantRequirementFormField,
): FieldPath<ContactDetails> {
  return `applicantRequirements.${index}.${field}` as FieldPath<ContactDetails>;
}

function applicantRequirementTabLabel(requirement: ApplicantRequirement, index: number): string {
  const intention = requirement.profileIntention?.trim();
  const externalId = requirement.externalId?.trim();
  if (intention && externalId) return `${intention} (${externalId})`;
  if (intention) return `${intention} ${index + 1}`;
  if (externalId) return externalId;
  return `Requirement ${index + 1}`;
}

function normalizeWireStringList(value: string | string[] | null | undefined): string[] {
  if (value == null) return [];
  if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
  const one = String(value).trim();
  return one ? [one] : [];
}

export function ContactDetailDrawer({
  open,
  contactId,
  onClose,
  onSaved,
  onUpdate,
  onCreate,
  onDeleted,
  onDeleteScheduled,
  onDeleteUndo,
}: Props) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [conflict, setConflict] = useState<ContactTransfer | null>(null);
  const deleteContactIds = useMemo(
    () => (contactId ? [contactId] : []),
    [contactId],
  );

  // A duplicate conflict is read from the contact's own crm_sync_error, so it is
  // refetched whenever the drawer opens on a different contact.
  useEffect(() => {
    if (!open || !contactId) {
      setConflict(null);
      return;
    }
    let cancelled = false;
    contactsService
      .getDuplicateConflict(contactId)
      .then((c) => {
        if (!cancelled) setConflict(c.duplicate ? c : null);
      })
      .catch((e: unknown) => {
        // Non-fatal: the banner is an aid, and its absence must not block editing.
        // Logged rather than swallowed — a silently missing banner looks identical to
        // "this contact has no conflict", which made an unreachable endpoint invisible.
        if (!cancelled) {
          console.warn("[contacts] duplicate-conflict lookup failed", e);
          setConflict(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, contactId]);

  const { legalEntityId, branchId: settingsBranchId } = useSettingsStore();
  const { countries: allCountries } = useCountries();
  const dialCodes = useMemo(() => allCountries.map((c) => c.code), [allCountries]);
  const [activeTab, setActiveTab] = useState("Contact");
  const [activeSubTab, setActiveSubTab] = useState("Contact");
  const [activeRequirementIndex, setActiveRequirementIndex] = useState(0);
  /**
   * The contact the form values currently belong to. The drawer is mounted once and
   * `contactId` changes underneath it, so form state outlives the contact it was reset
   * from; anything rendered from `watch()` must check this before trusting it.
   */
  const [formContactId, setFormContactId] = useState<string | null>(null);
  const [externalBranchId, setExternalBranchId] = useState<string | null>(null);
  const [branchSourceCrm, setBranchSourceCrm] = useState<string | null>(null);
  const [createBranchId, setCreateBranchId] = useState("");
  const [branchOptions, setBranchOptions] = useState<{ value: string; label: string }[]>([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const branchesByIdRef = useRef(
    new Map<string, { externalId: string; sourceCrm: string }>(),
  );
  const [createUserId, setCreateUserId] = useState("");
  const userRequired = isUserRequiredForCrm(branchSourceCrm);
  const [userOptions, setUserOptions] = useState<{ value: string; label: string }[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const loadedContactRef = useRef<ContactDetails | null>(null);
  const editBaselineRef = useRef<{
    form: ContactDetails;
    secondPersonEnabled: boolean;
  } | null>(null);
  const [editBaselineVersion, setEditBaselineVersion] = useState(0);
  const baselineUserIdRef = useRef("");
  const usersByIdRef = useRef(
    new Map<string, { externalId: string; name: string | null }>(),
  );
  const isSavingRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [secondPersonEnabled, setSecondPersonEnabled] = useState(false);
  // Existing contacts open with the address collapsed into a read-only preview; the fields
  // only unfold when the user clicks "Edit address". Create mode stays in edit mode always.
  const [addressEditing, setAddressEditing] = useState(false);
  const [furnishingOptions, setFurnishingOptions] = useState<Option[]>([]);
  const [positionOptions, setPositionOptions] = useState<Option[]>([]);
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    setError,
    clearErrors,
    trigger,
    formState: { errors },
    getValues,
  } = useForm<ContactDetails>({
    mode: "onTouched",
    reValidateMode: "onChange",
  });
  const { fields: persons, replace: replacePersons, append: appendPerson } = useFieldArray({
    control,
    name: "person",
  });
  const { data, loading, error, reload } = useResource(
    () =>
      contactId
        ? contactsService.getOne(contactId)
        : Promise.resolve(null),
    [contactId, open],
  );

  // Saving is a single call that can carry deletions with it, so the page loader
  // belongs to the save — removing a phone/email no longer hits the API on its own.
  const startPageLoading = useUiStore((s) => s.startPageLoading);
  const stopPageLoading = useUiStore((s) => s.stopPageLoading);

  useEffect(() => {
    if (isSaving) startPageLoading();
    else stopPageLoading();
    return () => stopPageLoading();
  }, [isSaving, startPageLoading, stopPageLoading]);

  /**
   * `useResource` blanks `data` while it refetches, which would swap the body for the
   * skeleton and reset the drawer's scroll position; the last loaded contact stands in
   * so the body stays mounted and the scroll holds.
   */
  const displayData = data ?? (loading ? loadedContactRef.current : null);

  const isCreateMode = !contactId;

  useEffect(() => {
    if (open) return;
    baselineUserIdRef.current = "";
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setActiveTab("Contact");
    setActiveSubTab("Contact");
    // Keyed on contactId too: the drawer stays mounted when it swaps contacts, and a
    // requirement index held over from the previous contact selects the wrong record.
    setActiveRequirementIndex(0);
  }, [open, contactId]);

  useEffect(() => {
    let mounted = true;
    Promise.all([contactsService.getFurnishings(), contactsService.getPositionTypes()])
      .then(([furnishings, positions]) => {
        if (mounted) {
          setFurnishingOptions(furnishings);
          setPositionOptions(positions);
        }
      })
      .catch((err) => console.error("Failed to load contact option lists", err));
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (open) return;
    isSavingRef.current = false;
    setIsSaving(false);
    setSubmitAttempted(false);
  }, [open]);

  /** Atlas branch UUIDs the contact belongs to (wire lists + optional `associations`). */
  const contactBranchIds = useMemo(() => {
    const assocList: BranchAssociations[] = data?.associations ?? [];
    const fromAssoc = assocList.map((a) => a.branchId).filter(Boolean) as string[];
    const fromWire = normalizeWireStringList(data?.branchId);
    return [...new Set([...fromAssoc, ...fromWire])].sort();
  }, [data?.associations, data?.branchId]);

  /** Stable key of the above, for effects that only care that the set changed. */
  const contactAssociationBranchIds = useMemo(() => contactBranchIds.join("|"), [contactBranchIds]);

  const countryCode = (watch("countryCode") ?? "").trim();
  const watchedCategory = watch("category");
  const firstForename = (watch("person.0.forename") ?? "").trim();
  const firstSurname = (watch("person.0.surname") ?? "").trim();
  // Rows the user removed are still in the form until save, so the "current" details
  // always come from the first row that is not flagged for deletion.
  const firstEmail = firstLiveEmailAddress(watch("person.0.emailAddresses"));
  const firstPhone = firstLivePhoneNumber(watch("person.0.phoneNumbers"));
  const secondSurname = (watch("person.1.surname") ?? "").trim();
  const secondEmail = firstLiveEmailAddress(watch("person.1.emailAddresses"));
  const secondPhone = firstLivePhoneNumber(watch("person.1.phoneNumbers"));
  // Every row counts, not just the first — the person only needs one of each.
  const firstHasEmail = hasAnyEmailAddress(watch("person.0.emailAddresses"));
  const firstHasPhone = hasAnyPhoneNumber(watch("person.0.phoneNumbers"));
  const secondHasEmail = hasAnyEmailAddress(watch("person.1.emailAddresses"));
  const secondHasPhone = hasAnyPhoneNumber(watch("person.1.phoneNumbers"));
  const postcode = (watch("postcode") ?? "").trim();
  const applicantRequirements = watch("applicantRequirements");
  const requirementsSupported = contactCategorySupportsRequirements(
    watchedCategory ?? data?.category,
  );
  const visibleSubTabs = SUBTABS;

  const visibleTabs = useMemo(
    () => TABS.filter((tab) => tab !== "Requirements" || requirementsSupported),
    [requirementsSupported],
  );
  const positionCode = watch("positionCode");
  const positionName = watch("positionName");
  const watchedFormValues = watch();

  const positionSelectOptions = useMemo(() => {
    const code = (positionCode ?? data?.positionCode ?? "").trim();
    const name = (positionName ?? data?.positionName ?? "").trim();
    if (!code) return positionOptions;
    const existing = positionOptions.find((o) => o.value === code);
    if (existing) {
      return existing.label === name || !name
        ? positionOptions
        : positionOptions.map((o) => (o.value === code ? { ...o, label: name } : o));
    }
    return [{ value: code, label: name || code }, ...positionOptions];
  }, [positionOptions, positionCode, positionName, data?.positionCode, data?.positionName]);

  const [uniquenessChecksEnabled, setUniquenessChecksEnabled] = useState(false);

  const uniquenessErrors = useContactFieldUniqueness({
    open: open && uniquenessChecksEnabled,
    excludeContactId: contactId,
    firstEmail,
    firstPhone,
    secondEmail,
    secondPhone,
    secondPersonEnabled,
  });

  useEffect(() => {
    const count = applicantRequirements?.length ?? 0;
    if (count > 0 && activeRequirementIndex >= count) {
      setActiveRequirementIndex(count - 1);
    }
  }, [applicantRequirements, activeRequirementIndex]);

  const handleSecondPersonEnabledChange = useCallback(
    (enabled: boolean) => {
      setSecondPersonEnabled(enabled);
      if (!enabled) {
        setValue("person.1", emptyPersonStub() as ContactDetails["person"][0], { shouldDirty: true });
        clearErrors([
          "person.1.forename",
          "person.1.surname",
          "person.1.emailAddresses.0.emailAddress",
          "person.1.phoneNumbers.0.number",
        ]);
      }
    },
    [setValue, clearErrors],
  );

  const coreFormFields = useMemo(
    () => ({
      countryCode,
      category: watchedCategory ?? "",
      forename: firstForename,
      surname: firstSurname,
      email: firstEmail,
      phone: firstPhone,
      postcode,
    }),
    [countryCode, watchedCategory, firstForename, firstSurname, firstEmail, firstPhone, postcode],
  );

  const formValidation = useMemo(() => {
    const personFields = {
      ...coreFormFields,
      secondSurname,
      secondEmail,
      secondPhone,
      secondPersonEnabled,
    };
    if (isCreateMode) {
      return validateContactCreateInput({
        ...personFields,
        legalEntityId: legalEntityId ?? "",
        branchId: createBranchId ?? "",
        externalBranchId: externalBranchId ?? "",
        sourceCrm: branchSourceCrm ?? "",
        userId: createUserId,
      });
    }
    // Updates additionally require each person to keep one email and one phone.
    return validateContactFormInput({
      ...personFields,
      hasEmail: firstHasEmail,
      hasPhone: firstHasPhone,
      secondHasEmail,
      secondHasPhone,
    });
  }, [
    isCreateMode,
    coreFormFields,
    legalEntityId,
    createBranchId,
    externalBranchId,
    branchSourceCrm,
    createUserId,
    secondSurname,
    secondEmail,
    secondPhone,
    secondPersonEnabled,
    firstHasEmail,
    firstHasPhone,
    secondHasEmail,
    secondHasPhone,
  ]);

  useEffect(() => {
    if (!open) return;
    // While the form still holds the previous contact, its category/requirements are not
    // this contact's — seeding or clearing off them would corrupt the incoming values.
    if (contactId && formContactId !== contactId) return;
    if (!requirementsSupported) {
      if (activeTab === "Requirements") setActiveTab("Contact");
      if (applicantRequirements?.length) {
        setValue("applicantRequirements", [], { shouldDirty: true });
      }
      return;
    }
    if (!applicantRequirements || applicantRequirements.length === 0) {
      setValue("applicantRequirements", [defaultApplicantRequirement()], { shouldDirty: false });
    }
  }, [open, contactId, formContactId, requirementsSupported, applicantRequirements, activeTab, setValue]);

  useEffect(() => {
    if (data && contactId) {
      loadedContactRef.current = data;
    }
    if (!open) {
      loadedContactRef.current = null;
    }
  }, [data, contactId, open]);

  const hasUserAssignmentChanged =
    Boolean(contactId) && createUserId !== baselineUserIdRef.current;

  const hasFormChangesFromBaseline = useMemo(() => {
    if (isCreateMode || !editBaselineRef.current) return false;
    return contactEditFormHasChanges(getValues(), editBaselineRef.current.form, {
      secondPersonEnabled,
      baselineSecondPersonEnabled: editBaselineRef.current.secondPersonEnabled,
    });
  }, [
    isCreateMode,
    editBaselineVersion,
    watchedFormValues,
    secondPersonEnabled,
    getValues,
  ]);

  const hasContactChanges = hasFormChangesFromBaseline || hasUserAssignmentChanged;
  const canSubmitContact =
    formValidation.success &&
    !hasContactInlineValidationErrors(uniquenessErrors) &&
    !isSaving &&
    (isCreateMode || (Boolean(data) && hasContactChanges));

  const submitDisabled =
    isSaving || data?.crmSyncStatus === "IN_PROGRESS" || (!isCreateMode && !canSubmitContact);

  /** Why the submit button is disabled — surfaced as a tooltip, since a disabled button explains nothing. */
  const submitBlockedReason = useMemo(() => {
    if (isSaving) return "";
    if (data?.crmSyncStatus === "IN_PROGRESS")
      return `${crmDisplayName(data?.sourceCrm)} sync is in progress. Try again once it finishes.`;
    if (!formValidation.success) return formValidation.message;
    if (hasContactInlineValidationErrors(uniquenessErrors)) {
      return "Another contact already uses one of these details. Resolve the highlighted fields.";
    }
    if (!isCreateMode && Boolean(data) && !hasContactChanges) return "No changes to save";
    return "";
  }, [isSaving, data, formValidation, uniquenessErrors, isCreateMode, hasContactChanges]);

  /**
   * What the save will actually do, in the user's words — deletions in particular only
   * happen on save now, so the button has to say what is queued up.
   */
  const pendingChangeSummary = useMemo(() => {
    if (isCreateMode || !editBaselineRef.current || !hasContactChanges) return "";
    const lines = describeContactEditChanges(getValues(), editBaselineRef.current.form, {
      secondPersonEnabled,
      baselineSecondPersonEnabled: editBaselineRef.current.secondPersonEnabled,
    });
    if (hasUserAssignmentChanged) lines.push("Changed the assigned user");
    if (!lines.length) return "";
    return ["This save will:", ...lines.map((line) => `• ${line}`)].join("\n");
  }, [
    isCreateMode,
    editBaselineVersion,
    watchedFormValues,
    hasContactChanges,
    hasUserAssignmentChanged,
    secondPersonEnabled,
    getValues,
  ]);

  const applyCreateBranch = useCallback((branchId: string) => {
    setCreateBranchId(branchId);
    const meta = branchesByIdRef.current.get(branchId);
    setExternalBranchId(meta?.externalId ?? null);
    setBranchSourceCrm(meta?.sourceCrm ?? null);
  }, []);

  useEffect(() => {
    if (!open || contactId || !legalEntityId) {
      setCreateBranchId("");
      setBranchOptions([]);
      setExternalBranchId(null);
      setBranchSourceCrm(null);
      branchesByIdRef.current.clear();
      return;
    }

    let cancelled = false;
    setLoadingBranches(true);
    branchesService
      .list({ legalEntityId }, { page: 0, size: 200 })
      .then((res) => {
        if (cancelled) return;
        const pool = new Map<string, { externalId: string; sourceCrm: string }>();
        const options = res.content.map((b) => {
          pool.set(b.id, {
            externalId: b.externalId ?? "",
            sourceCrm: b.sourceCrm?.trim() ?? "",
          });
          return {
            value: b.id,
            label: b.name?.trim() || b.externalId?.trim() || b.id,
          };
        });
        branchesByIdRef.current = pool;
        setBranchOptions(options);

        const preferred =
          settingsBranchId && pool.has(settingsBranchId)
            ? settingsBranchId
            : options[0]?.value ?? "";
        if (preferred) {
          setCreateBranchId(preferred);
          const meta = pool.get(preferred);
          setExternalBranchId(meta?.externalId ?? null);
          setBranchSourceCrm(meta?.sourceCrm ?? null);
        } else {
          setCreateBranchId("");
          setExternalBranchId(null);
          setBranchSourceCrm(null);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setBranchOptions([]);
        setCreateBranchId("");
        setExternalBranchId(null);
        setBranchSourceCrm(null);
        branchesByIdRef.current.clear();
      })
      .finally(() => {
        if (!cancelled) setLoadingBranches(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, contactId, legalEntityId, settingsBranchId]);

  const userListLegalEntityId = legalEntityId || data?.legalEntityId || "";

  useEffect(() => {
    if (!open || !userListLegalEntityId) {
      setCreateUserId("");
      setUserOptions([]);
      usersByIdRef.current.clear();
      return;
    }

    let cancelled = false;
    setLoadingUsers(true);
    usersService
      .listNegotiators({ legalEntityId: userListLegalEntityId }, { page: 0, size: 200 })
      .then((res) => {
        if (cancelled) return;
        const pool = new Map<string, { externalId: string; name: string | null }>();
        const options = (res.content ?? []).map((u) => {
          pool.set(u.id, {
            externalId: u.externalId ?? "",
            name: u.name?.trim() || null,
          });
          const label =
            u.name?.trim() ||
            u.email?.trim() ||
            u.externalId?.trim() ||
            u.id;
          return { value: u.id, label };
        });
        usersByIdRef.current = pool;
        setUserOptions(options);

        if (!contactId) {
          setCreateUserId(options[0]?.value ?? "");
        }
      })
      .catch(() => {
        if (cancelled) return;
        setUserOptions([]);
        setCreateUserId("");
        usersByIdRef.current.clear();
      })
      .finally(() => {
        if (!cancelled) setLoadingUsers(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, contactId, userListLegalEntityId]);

  useEffect(() => {
    if (!open || !contactId || !data || loadingUsers || userOptions.length === 0) return;
    const resolvedUserId = resolveContactUserAtlasId(
      data,
      data.lead ?? null,
      usersByIdRef.current,
    );
    setCreateUserId(resolvedUserId);
    baselineUserIdRef.current = resolvedUserId;
  }, [open, contactId, data, loadingUsers, userOptions.length]);

  const [resolvedAssociationBranchText, setResolvedAssociationBranchText] = useState<string | null>(
    null,
  );
  const [associationBranchLabelsReady, setAssociationBranchLabelsReady] = useState(false);

  useEffect(() => {
    if (!open) {
      setResolvedAssociationBranchText(null);
      setAssociationBranchLabelsReady(false);
      return;
    }
    if (!contactId || !contactAssociationBranchIds) {
      setResolvedAssociationBranchText(null);
      setAssociationBranchLabelsReady(true);
      return;
    }
    const uniqueIds = contactAssociationBranchIds.split("|").filter(Boolean);
    if (uniqueIds.length === 0) {
      setResolvedAssociationBranchText(null);
      setAssociationBranchLabelsReady(true);
      return;
    }

    let cancelled = false;
    setAssociationBranchLabelsReady(false);
    Promise.all(
      uniqueIds.map((id) =>
        branchesService
          .getOne(id)
          .then((b) => b.name?.trim() || b.externalId?.trim() || null)
          .catch(() => null),
      ),
    ).then((parts) => {
      if (cancelled) return;
      const labels = parts.filter((p): p is string => Boolean(p));
      setResolvedAssociationBranchText(labels.length ? labels.join(", ") : null);
      setAssociationBranchLabelsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [open, contactId, contactAssociationBranchIds]);

  const branches: BranchAssociations[] = data?.associations || [];
  const associationExternalBranchLabels = useMemo(() => {
    const fromAssoc = branches
      .map((b) => b.externalBranchId)
      .filter((x): x is string => typeof x === "string" && Boolean(x.trim()))
      .map((x) => x.trim());
    const fromWire = normalizeWireStringList(data?.externalBranchId);
    return [...new Set([...fromAssoc, ...fromWire])].join(", ");
  }, [branches, data?.externalBranchId]);

  /** Wait for Atlas branch lookups before falling back — avoids flashing CRM branch numbers. */
  const associationAtlasBranchIdCount =
    Boolean(contactId) &&
    typeof contactAssociationBranchIds === "string" &&
    contactAssociationBranchIds.length > 0
      ? contactAssociationBranchIds.split("|").filter(Boolean).length
      : 0;

  const awaitingBranchNameResolution =
    Boolean(contactId) && associationAtlasBranchIdCount > 0 && !associationBranchLabelsReady;

  const branchReadOnlyDisplay = awaitingBranchNameResolution
    ? undefined
    : resolvedAssociationBranchText || associationExternalBranchLabels || undefined;
  const onSubmit = async ({ associations, users: _users, ...form }: ContactDetails) => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    setIsSaving(true);

    const selectedUserMeta = createUserId
      ? usersByIdRef.current.get(createUserId)
      : undefined;
    const assignedUser = createUserId
      ? {
          id: createUserId,
          externalId: selectedUserMeta?.externalId ?? "",
          name: selectedUserMeta?.name ?? null,
        }
      : null;
    try {
      if (hasContactInlineValidationErrors(uniquenessErrors)) {
        applyContactInlineValidationToForm(setError, clearErrors, uniquenessErrors);
        return;
      }

      if (!contactId) {
        if (!legalEntityId) {
          toast.error("Select a legal entity in the header");
          return;
        }

        const validation = validateContactCreateInput({
          legalEntityId: legalEntityId ?? "",
          branchId: createBranchId ?? "",
          externalBranchId: externalBranchId ?? "",
          sourceCrm: branchSourceCrm ?? "",
          userId: createUserId,
          countryCode: form.countryCode?.trim() ?? "",
          category: form.category ?? "",
          forename: form.person?.[0]?.forename?.trim() ?? "",
          surname: form.person?.[0]?.surname?.trim() ?? "",
          email: form.person?.[0]?.emailAddresses?.[0]?.emailAddress?.trim() ?? "",
          phone: firstLivePhoneNumber(form.person?.[0]?.phoneNumbers),
          postcode: form.postcode?.trim() ?? "",
          secondSurname: secondPersonEnabled ? form.person?.[1]?.surname?.trim() ?? "" : "",
          secondEmail: secondPersonEnabled ? form.person?.[1]?.emailAddresses?.[0]?.emailAddress?.trim() ?? "" : "",
          secondPhone: secondPersonEnabled ? firstLivePhoneNumber(form.person?.[1]?.phoneNumbers) : "",
          secondPersonEnabled,
        });

        if (!validation.success) {
          return;
        }

        const payload = buildContactCreatePayload(form, {
          legalEntityId: legalEntityId!,
          branchId: createBranchId!,
          externalBranchId: externalBranchId!,
          sourceCrm: branchSourceCrm,
          user: assignedUser,
          existingUsers: _users,
          secondPersonEnabled,
        });

        const created = await contactsService.create(payload);
        const summary = enrichContactSummary(created, payload);
        onClose();
        onCreate?.(summary);
        toast.success("Contact created successfully!", { toastId: "contact-create-success" });
        return;
      }
      // Rows flagged for deletion are already out of play, so they are excluded here too.
      const validation = validateContactFormInput({
        countryCode: form.countryCode?.trim() ?? "",
        category: form.category ?? "",
        forename: form.person?.[0]?.forename?.trim() ?? "",
        surname: form.person?.[0]?.surname?.trim() ?? "",
        email: firstLiveEmailAddress(form.person?.[0]?.emailAddresses),
        phone: firstLivePhoneNumber(form.person?.[0]?.phoneNumbers),
        postcode: form.postcode?.trim() ?? "",
        secondSurname: secondPersonEnabled ? form.person?.[1]?.surname?.trim() ?? "" : "",
        secondEmail: secondPersonEnabled ? firstLiveEmailAddress(form.person?.[1]?.emailAddresses) : "",
        secondPhone: secondPersonEnabled ? firstLivePhoneNumber(form.person?.[1]?.phoneNumbers) : "",
        secondPersonEnabled,
        hasEmail: hasAnyEmailAddress(form.person?.[0]?.emailAddresses),
        hasPhone: hasAnyPhoneNumber(form.person?.[0]?.phoneNumbers),
        secondHasEmail: hasAnyEmailAddress(form.person?.[1]?.emailAddresses),
        secondHasPhone: hasAnyPhoneNumber(form.person?.[1]?.phoneNumbers),
      });

      if (!validation.success) {
        toast.error(validation.message);
        return;
      }

      if (!hasContactChanges) {
        return;
      }

      const patch = buildContactUpdatePatch(form, data ?? undefined, { secondPersonEnabled });
      const updated = await contactsService.update(contactId, patch);
      const summary = contactDetailsToSummary(updated as ContactDetails);
      editBaselineRef.current = null;
      onClose();
      onUpdate(summary);
      toast.success("Contact updated successfully!", { toastId: "contact-update-success" });
      onSaved?.();
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      const fallback = contactId
        ? "Failed to update contact. Please try again."
        : "Failed to create contact. Please try again.";
      // Toast mirrors the success path, so both outcomes land in the same place.
      toast.error(apiMessage ?? fallback, { toastId: "contact-save-error" });
      if (apiMessage) {
        applyContactApiValidationToForm(setError, apiMessage);
        showActionError(apiMessage, { title: "Could not save contact" });
      } else {
        showActionErrorFromUnknown(err, fallback, { title: "Could not save contact" });
      }
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  const submitContactForm = handleSubmit(onSubmit);

  const handleSubmitClick = () => {
    if (isSavingRef.current) return;
    if (isCreateMode) setSubmitAttempted(true);
    void submitContactForm();
  };

  const showCreateValidation = isCreateMode && submitAttempted;

  useEffect(() => {
    if (!open) {
      setUniquenessChecksEnabled(false);
      setAddressEditing(false);
      return;
    }

    if (!contactId) {
      const defaults = getEmptyContactFormDefaults();
      defaults.intentionCode = "0";
      defaults.intentionName = intentionNameFromCode("0");
      editBaselineRef.current = null;
      setEditBaselineVersion((v) => v + 1);
      reset(defaults);
      replacePersons(defaults.person as Parameters<typeof replacePersons>[0]);
      setCreateUserId("");
      baselineUserIdRef.current = "";
      setSecondPersonEnabled(false);
      clearErrors();
      applyContactInlineValidationToForm(setError, clearErrors, EMPTY_CONTACT_INLINE_ERRORS);
      setUniquenessChecksEnabled(true);
      setFormContactId(null);
      // Create mode is always in edit-fields state — nothing to preview yet.
      setAddressEditing(true);
      return;
    }

    if (!data || (data.id && data.id !== contactId)) {
      editBaselineRef.current = null;
      setUniquenessChecksEnabled(false);
      return;
    }

    const normalized = normalizeContactForForm(data);
    if (dialCodes.length > 0) {
      for (const person of normalized.person ?? []) {
        for (const phone of person.phoneNumbers ?? []) {
          const { dialCode, national } = extractDialCode(phone.number, dialCodes);
          if (dialCode) {
            phone.number = national;
            (phone as any).countryDialCode = dialCode;
          }
        }
      }
    }
    const second = data.person?.[1];
    const secondEnabled = Boolean(second && personHasContent(second));
    editBaselineRef.current = { form: normalized, secondPersonEnabled: secondEnabled };
    setEditBaselineVersion((v) => v + 1);
    reset(normalized);
    setFormContactId(contactId);
    replacePersons(normalized.person as Parameters<typeof replacePersons>[0]);
    setSecondPersonEnabled(secondEnabled);
    clearErrors();
    applyContactInlineValidationToForm(setError, clearErrors, EMPTY_CONTACT_INLINE_ERRORS);
    setUniquenessChecksEnabled(true);
    // Freshly loaded existing contact — collapse the address back to the read-only preview.
    setAddressEditing(false);
  }, [open, contactId, data, dialCodes, reset, replacePersons, clearErrors, setError]);

  useEffect(() => {
    if (!open || !uniquenessChecksEnabled) return;
    applyContactInlineValidationToForm(setError, clearErrors, uniquenessErrors);
  }, [open, uniquenessChecksEnabled, uniquenessErrors, setError, clearErrors]);

  useEffect(() => {
    if (!open || persons.length >= 2) return;
    appendPerson(emptyPersonStub() as any);
  }, [open, persons.length, appendPerson]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={contactId ? (displayData?.name ?? displayData?.email ?? "Contact Details") : "New Contact"}
      subtitle={contactId ? (
        <span className="group/copy inline-flex items-center gap-1">
          <span>ID: {contactId}</span>
          <CopyButton
            value={contactId}
            label="Contact ID"
            successMessage="Record ID copied to clipboard"
          />
        </span>
      ) : undefined}
      width="2xl"
      actions={
        <div className="flex w-full items-center justify-end gap-2">
          {contactId && onDeleted && (
            <>
              {/* Left of the divider: destructive, and deliberately far from Update. */}
              <Tooltip
                content={
                  data?.crmSyncStatus === "IN_PROGRESS"
                    ? "Cannot delete while a CRM sync is in progress"
                    : ""
                }
                side="top"
              >
                <span className="inline-flex">
                  <Button
                    variant="danger"
                    type="button"
                    disabled={isSaving || data?.crmSyncStatus === "IN_PROGRESS"}
                    onClick={() => setDeleteDialogOpen(true)}
                  >
                    <Trash2 size={14} /> Delete
                  </Button>
                </span>
              </Tooltip>
              <div className="flex-1" />
            </>
          )}
          {data?.crmSyncStatus === "IN_PROGRESS" && (
            <span className="mr-2 flex items-center gap-2 text-sm text-bridge-muted">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75"></span>
                <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-500"></span>
              </span>
              {crmDisplayName(data?.sourceCrm)} sync in progress...
            </span>
          )}
          <Button variant="ghost" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          {/* A disabled button swallows mouse events, so the tooltip hangs off a wrapper span. */}
          <Tooltip content={submitDisabled ? submitBlockedReason : pendingChangeSummary} side="top">
            <span className="inline-flex">
              <Button
                variant="primary"
                type="button"
                disabled={submitDisabled}
                loading={isSaving}
                onClick={handleSubmitClick}
              >
                {contactId ? "Update contact" : "Create contact"}
              </Button>
            </span>
          </Tooltip>
        </div>
      }
    >
      {conflict && contactId && (
        <DuplicateConflictBanner
          conflict={conflict}
          disabled={isSaving}
          onTransfer={() => setTransferDialogOpen(true)}
          onDelete={() => setDeleteDialogOpen(true)}
        />
      )}
      {contactId && onDeleted && (
        <>
          <ContactTransferDialog
            open={transferDialogOpen}
            contactId={contactId}
            suggested={conflict?.target ?? null}
            suggestedExternalId={conflict?.duplicateExternalId ?? null}
            onClose={() => setTransferDialogOpen(false)}
            onTransferred={(result) => {
              toast.success(
                `Moved ${result.notesTransferred} note(s) and ${result.diaryEntriesTransferred} diary entry/entries ` +
                  `to ${result.target?.name ?? result.target?.externalId ?? "the target contact"}.`,
              );
              onSaved?.();
            }}
          />
          <ContactDeleteDialog
            open={deleteDialogOpen}
            contactIds={deleteContactIds}
            contactLabels={{ [contactId]: displayData?.name ?? contactId }}
            onClose={() => setDeleteDialogOpen(false)}
            onDeleteScheduled={onDeleteScheduled}
            onDeleteUndo={onDeleteUndo}
            onDeleted={(impact) => {
              onDeleted?.(impact);
              onClose();
            }}
          />
        </>
      )}
      {loading && !displayData && (
        <div className="space-y-3">
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-[400px] w-full" />
        </div>
      )}
      {error && <div className="text-sm text-bridge-danger">{error.message}</div>}
      {(displayData || !contactId) && (
        <div className="flex h-full flex-col gap-4 md:flex-row md:gap-6">
          {/* Main Sidebar for Tabs */}
          <aside className="flex shrink-0 flex-row gap-1 overflow-x-auto border-b border-bridge-border/50 pb-2 md:w-44 md:flex-col md:overflow-visible md:border-b-0 md:border-r md:pr-4 lg:w-48">
            {visibleTabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`whitespace-nowrap rounded-md px-3 py-2 text-left text-sm transition-colors md:px-4 ${
                  activeTab === tab
                    ? "bg-[#009BF5] text-white font-medium shadow-sm"
                    : "text-bridge-muted hover:bg-bridge-surface-hover hover:text-bridge-fg"
                }`}
              >
                {tab}
              </button>
            ))}
          </aside>

          {/* Main Content Area */}
          <div
            className={cn(
              "flex-1 pb-1",
              activeTab === "Requirements" ? "overflow-visible" : "overflow-y-auto",
            )}
          >
            {activeTab === "Contact" ? (
              <div className="flex flex-col h-full bg-white rounded-md shadow-sm border border-bridge-border/40">
                {/* Horizontal Sub-Tabs */}
                <div className="flex overflow-x-auto border-b border-bridge-border/50 bg-gray-50/50 rounded-t-md px-2">
                  {visibleSubTabs.map((subtab) => (
                    <button
                      key={subtab}
                      onClick={() => setActiveSubTab(subtab)}
                      className={`shrink-0 whitespace-nowrap px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                        activeSubTab === subtab
                          ? "border-[#009BF5] text-[#009BF5]"
                          : "border-transparent text-bridge-muted hover:text-bridge-fg"
                      }`}
                    >
                      {subtab}
                    </button>
                  ))}
                </div>

                <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                  {activeSubTab === "Contact" ? (
                      <form
                        id="contact-detail-form"
                        onSubmit={(event) => {
                          event.preventDefault();
                          handleSubmitClick();
                        }}
                        autoComplete="off"
                      >
                    <p className="mb-5 text-xs text-bridge-muted">
                      <span className="text-red-500">*</span> Required field
                    </p>
                    {displayData?.crmSyncStatus === "FAILED" && displayData?.crmSyncError && (
                      <div className="mb-5 p-4 rounded-md bg-red-50 border border-red-200">
                        <div className="flex gap-3">
                          <svg className="w-5 h-5 text-red-600 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          <div>
                            <h3 className="text-sm font-medium text-red-800">CRM Sync Failed</h3>
                            <p className="mt-1 text-sm text-red-700">{friendlyCrmSyncError(displayData.crmSyncError)}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="mx-auto w-full max-w-6xl space-y-4">
                      <ContactFormSection>
                        <ContactFormRow>
                          <Field label="Type">
                            {isCreateMode ? (
                              <MenuSelect
                                label="Type"
                                value={watch("category") ?? ""}
                                onChange={(v) => {
                                  setValue("category", v as ContactDetails["category"], { shouldDirty: true });
                                  if (!contactCategorySupportsRequirements(v)) {
                                    setValue("applicantRequirements", [], { shouldDirty: true });
                                    setActiveTab("Contact");
                                  }
                                }}
                                options={CONTACT_TYPE_OPTIONS}
                                placeholder="Select type"
                                hideLabel
                                enableSearch
                                searchInTrigger
                                searchPlaceholder="Search type…"
                                searchDebounceMs={200}
                              />
                            ) : (
                              <EditLocked>
                                <ReadOnlyValue
                                  value={
                                    CONTACT_TYPE_OPTIONS.find((o) => o.value === watch("category"))
                                      ?.label ?? watch("category")
                                  }
                                  fallback="Not specified"
                                />
                              </EditLocked>
                            )}
                          </Field>
                          <Field label="Branch" required={isCreateMode}>
                            {isCreateMode ? (
                              loadingBranches ? (
                                <Skeleton className="min-h-[40px] w-full rounded-md" />
                              ) : (
                                <MenuSelect
                                  hideLabel
                                  value={createBranchId}
                                  onChange={(v) => applyCreateBranch(v)}
                                  options={branchOptions}
                                  placeholder="Select branch"
                                  disabled={loadingBranches || branchOptions.length === 0}
                                  enableSearch
                                  searchInTrigger
                                  searchPlaceholder="Search branch…"
                                />
                              )
                            ) : awaitingBranchNameResolution ? (
                              <Skeleton className="min-h-[40px] w-full rounded-md" />
                            ) : (
                              <EditLocked>
                                <ReadOnlyValue
                                  value={branchReadOnlyDisplay}
                                  fallback="Not specified"
                                />
                              </EditLocked>
                            )}
                            <FieldValidationError
                              show={showCreateValidation && !createBranchId.trim()}
                              message="Select a branch"
                            />
                            <FieldValidationError
                              show={
                                showCreateValidation &&
                                Boolean(createBranchId.trim()) &&
                                !externalBranchId?.trim()
                              }
                              message="Branch external id is missing"
                            />
                            <FieldValidationError
                              show={
                                showCreateValidation &&
                                Boolean(createBranchId.trim()) &&
                                Boolean(externalBranchId?.trim()) &&
                                !branchSourceCrm?.trim()
                              }
                              message="Source CRM was not returned for this branch. Wait for data to load, or verify the branch in the API."
                            />
                          </Field>
                        </ContactFormRow>

                        <ContactFormRow>
                          <Field label="Intention">
                            <EditLocked locked={!isCreateMode}>
                              <MenuSelect
                                hideLabel
                                value={watch("intentionCode") ?? "0"}
                                onChange={(v) => {
                                  setValue("intentionCode", v ?? null, { shouldDirty: true });
                                  setValue("intentionName", intentionNameFromCode(v ?? null), {
                                    shouldDirty: true,
                                  });
                                }}
                                options={INTENTION_OPTIONS}
                                placeholder="Not specified"
                                disabled={!isCreateMode}
                              />
                            </EditLocked>
                          </Field>
                          <Field label="User" required={isCreateMode && userRequired}>
                            <EditLocked locked={!isCreateMode}>
                              {loadingUsers ? (
                                <Skeleton className="min-h-[40px] w-full rounded-md" />
                              ) : (
                                <MenuSelect
                                  hideLabel
                                  value={createUserId}
                                  onChange={(v) => setCreateUserId(v)}
                                  options={userOptions}
                                  placeholder={
                                    userOptions.length === 0 ? "No negotiators available" : "Select user"
                                  }
                                  disabled={loadingUsers || !isCreateMode || userOptions.length === 0}
                                  enableSearch
                                  searchInTrigger
                                  searchPlaceholder="Search user…"
                                />
                              )}
                            </EditLocked>
                            <FieldValidationError
                              show={showCreateValidation && userRequired && !createUserId.trim()}
                              message={USER_REQUIRED_MESSAGE}
                            />
                          </Field>
                        </ContactFormRow>

                        <ContactFormRow>
                          <Field label="Lead Source">
                            {isCreateMode ? (
                              <MenuSelect
                                hideLabel
                                value={watch("leadSourceCode") ?? "0"}
                                onChange={(v) => {
                                  setValue("leadSourceCode", v ?? null, { shouldDirty: true });
                                  setValue("leadSourceName", leadSourceNameFromCode(v ?? null), {
                                    shouldDirty: true,
                                  });
                                }}
                                options={LEAD_SOURCE_OPTIONS}
                                placeholder="Not specified"
                                disabled
                              />
                            ) : (
                              <EditLocked>
                                <ReadOnlyValue
                                  value={
                                    leadSourceNameFromCode(
                                      watch("leadSourceCode") ?? displayData?.leadSourceCode ?? "0",
                                    ) || "Not specified"
                                  }
                                />
                              </EditLocked>
                            )}
                          </Field>
                          <Field label="Company">
                            <EditLocked locked={!isCreateMode}>
                              <Input
                                {...register("companyName")}
                                className="w-full"
                                disabled={!isCreateMode}
                                readOnly={!isCreateMode}
                              />
                            </EditLocked>
                          </Field>
                        </ContactFormRow>

                        <Field label="Contact Note" multiline>
                          <EditLocked locked={!isCreateMode}>
                            <TextArea
                              rows={4}
                              className="w-full"
                              {...register("notes")}
                              disabled={!isCreateMode}
                              readOnly={!isCreateMode}
                            />
                          </EditLocked>
                        </Field>
                      </ContactFormSection>

                      <ContactFormSection>
                        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-x-6">
                          <PersonSection
                            title="First Person"
                            personIndex={0}
                            isPrimary
                            requireOnlySurname
                            validatePhoneIfProvided={isCreateMode}
                            submitAttempted={submitAttempted}
                            requireContactPoint={!isCreateMode}
                            emailInline={uniquenessErrors.email}
                            phoneInline={uniquenessErrors.mobilePhone}
                            control={control}
                            register={register}
                            watch={watch}
                            setValue={setValue}
                            getValues={getValues}
                            errors={errors}
                          />
                          <PersonSection
                            title="Second Person (Optional)"
                            personIndex={1}
                            isPrimary={false}
                            requireOnlySurname
                            validatePhoneIfProvided={isCreateMode}
                            secondPersonEnabled={secondPersonEnabled}
                            showEnableToggle
                            enabled={secondPersonEnabled}
                            onEnabledChange={handleSecondPersonEnabledChange}
                            submitAttempted={submitAttempted}
                            requireContactPoint={!isCreateMode}
                            emailInline={uniquenessErrors.email2}
                            phoneInline={uniquenessErrors.mobilePhone2}
                            control={control}
                            register={register}
                            watch={watch}
                            setValue={setValue}
                            getValues={getValues}
                            errors={errors}
                          />
                        </div>
                        <ContactFieldRow label="Salutation" alignTop className="mt-2">
                          <EditLocked locked={!isCreateMode}>
                            <TextArea
                              rows={3}
                              placeholder={buildDefaultSalutation(watch("person")) || "Dear …"}
                              className="w-full"
                              {...register("salutationLine")}
                              disabled={!isCreateMode}
                              readOnly={!isCreateMode}
                            />
                          </EditLocked>
                        </ContactFieldRow>
                      </ContactFormSection>

                      <ContactFormSection title="Address">
                        {!isCreateMode && !addressEditing ? (
                          <AddressPreview
                            addressSubDwelling={watch("addressSubDwelling") ?? ""}
                            addressNameNumber={watch("addressNameNumber") ?? ""}
                            street={watch("street") ?? ""}
                            locality={watch("locality") ?? ""}
                            town={watch("town") ?? ""}
                            county={watch("county") ?? ""}
                            postcode={watch("postcode") ?? ""}
                            actionInFlow
                            action={
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setAddressEditing(true)}
                              >
                                Edit address
                              </Button>
                            }
                          />
                        ) : (
                          <>
                            <ContactFormRow>
                              <Field label="Sub Dwelling">
                                <Input {...register("addressSubDwelling")} className="w-full" />
                              </Field>
                              <Field label="Name/Number">
                                <Input {...register("addressNameNumber")} className="w-full" />
                              </Field>
                            </ContactFormRow>
                            <ContactFormRow>
                              <Field label="Street">
                                <div className="w-full space-y-1">
                                  <Input {...register("street")} className="w-full" />
                                  <FormInlineError message={errors.street?.message} />
                                </div>
                              </Field>
                              <Field label="Locality">
                                <Input {...register("locality")} className="w-full" />
                              </Field>
                            </ContactFormRow>
                            <ContactFormRow>
                              <Field label="Town">
                                <Input {...register("town")} className="w-full" />
                              </Field>
                              <Field label="County">
                                <Input {...register("county")} className="w-full" />
                              </Field>
                            </ContactFormRow>
                            <ContactFormRow>
                              <Field label="Postcode">
                                <div className="w-full space-y-1">
                                  <Input {...register("postcode")} className="w-full" />
                                  <FormInlineError message={errors.postcode?.message} />
                                </div>
                              </Field>
                              <Field label="Country Code">
                                <CountryCodeSelect
                                  value={watch("countryCode") ?? ""}
                                  onChange={(v) => setValue("countryCode", v, { shouldDirty: true })}
                                />
                                <FieldValidationError
                                  show={
                                    showCreateValidation &&
                                    Boolean(countryCode) &&
                                    !COUNTRY_CODE_REGEX.test(countryCode)
                                  }
                                  message={COUNTRY_CODE_VALIDATION_MESSAGE}
                                />
                              </Field>
                            </ContactFormRow>
                            <ContactFormRow>
                              <Field label="UPRN">
                                <Input
                                  {...register("uprn", {
                                    maxLength: {
                                      value: 12,
                                      message: "UPRN must be at most 12 characters",
                                    },
                                    setValueAs: (v) => (typeof v === "string" ? v.trim() : v),
                                  })}
                                  className="w-full"
                                  maxLength={12}
                                  placeholder="Unique Property Reference Number"
                                />
                              </Field>
                              <div className="hidden min-w-0 lg:block" aria-hidden />
                            </ContactFormRow>
                          </>
                        )}
                      </ContactFormSection>
                    </div>
                      </form>
                  ) : activeSubTab === "Position" ? (
                    <div className="mx-auto w-full max-w-4xl space-y-4">
                      <div className="rounded-md border border-bridge-border/40 bg-white p-4 shadow-sm">
                        <h3 className="text-sm font-semibold text-bridge-fg mb-3">Position</h3>
                        <Field label="Position" className="mb-4">
                          <MenuSelect
                            hideLabel
                            value={watch("positionCode") ?? ""}
                            onChange={(v) => {
                              setValue("positionCode", v || null, { shouldDirty: true });
                              setValue(
                                "positionName",
                                positionSelectOptions.find((o) => o.value === v)?.label ?? null,
                                { shouldDirty: true },
                              );
                            }}
                            options={positionSelectOptions}
                            placeholder="Select position"
                          />
                        </Field>
                        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          {[
                            {
                              label: "Intention name",
                              value: contactFieldDisplay(watch("intentionName"), displayData?.intentionName),
                            },
                            {
                              label: "Disposal name",
                              value: contactFieldDisplay(watch("disposalName"), displayData?.disposalName),
                            },
                            {
                              label: "Potential vendor",
                              value:
                                displayData?.potentialVendor == null ? "-" : displayData.potentialVendor ? "Yes" : "No",
                            },
                            {
                              label: "Potential landlord",
                              value:
                                displayData?.potentialLandlord == null ? "-" : displayData.potentialLandlord ? "Yes" : "No",
                            },
                          ].map((row) => (
                            <div key={row.label} className="min-w-0">
                              <dt className="text-xs text-bridge-muted">{row.label}</dt>
                              <dd className="text-sm font-medium text-bridge-fg break-words">{row.value || "-"}</dd>
                            </div>
                          ))}
                        </dl>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full text-bridge-muted">
                      {activeSubTab} details will be displayed here.
                    </div>
                  )}
                </div>
              </div>
            ) : activeTab === "Timeline" ? (
              <div className="flex flex-col overflow-hidden rounded-md border border-bridge-border/40 bg-white shadow-sm">
                {contactId ? (
                  <FileNotesPanel
                    active={activeTab === "Timeline"}
                    contactId={contactId}
                    contactNotes={displayData?.contactNotes}
                    onNoteCreated={reload}
                  />
                ) : (
                  <p className="p-4 text-sm text-bridge-muted sm:p-6">Timeline will be displayed here.</p>
                )}
              </div>
            ) : activeTab === "Requirements" ? (
              <div className="overflow-visible rounded-md border border-bridge-border/40 bg-white shadow-sm">
                <div className="flex items-center justify-between px-4 pt-4 sm:px-6 sm:pt-6">
                  <h3 className="text-base font-semibold text-bridge-fg">Applicant Requirements</h3>
                  {contactId && loading ? <Skeleton className="h-5 w-20" /> : null}
                </div>
                {contactId && formContactId !== contactId ? (
                  // Form state still belongs to the previously opened contact — rendering it
                  // here showed that contact's requirement External ID under this one.
                  <div className="space-y-4 p-4 sm:p-6">
                    <Skeleton className="h-6 w-40" />
                    <Skeleton className="h-24 w-full" />
                  </div>
                ) : (() => {
                  const requirements = applicantRequirements ?? [];
                  const requirementCount = requirements.length;
                  const reqIndex =
                    requirementCount > 0
                      ? Math.min(activeRequirementIndex, requirementCount - 1)
                      : 0;
                  const activeRequirement = requirements[reqIndex] ?? defaultApplicantRequirement();
                  const buyIntention = isBuyApplicantIntention(activeRequirement.profileIntention);
                  const hasIntention = hasApplicantRequirementIntention(
                    activeRequirement.profileIntention,
                  );
                  const reqField = (field: ApplicantRequirementFormField) =>
                    applicantRequirementFieldPath(reqIndex, field);
                  return (
                    <>
                      {requirementCount > 1 ? (
                        <div
                          className="mt-3 flex overflow-x-auto border-b border-bridge-border/50 bg-gray-50/50 px-2 sm:px-4"
                          role="tablist"
                          aria-label="Applicant requirements"
                        >
                          {requirements.map((requirement, index) => (
                            <button
                              key={requirement.id || `requirement-${index}`}
                              type="button"
                              role="tab"
                              aria-selected={reqIndex === index}
                              onClick={() => setActiveRequirementIndex(index)}
                              className={cn(
                                "shrink-0 whitespace-nowrap px-4 py-3 text-sm font-medium border-b-2 transition-colors",
                                reqIndex === index
                                  ? "border-[#009BF5] text-[#009BF5]"
                                  : "border-transparent text-bridge-muted hover:text-bridge-fg",
                              )}
                            >
                              {applicantRequirementTabLabel(requirement, index)}
                            </button>
                          ))}
                        </div>
                      ) : null}
                      <div className="space-y-4 overflow-visible p-4 pb-20 sm:p-6 sm:pb-24">
                        {contactId ? (
                          <ApplicantRequirementSummary requirement={activeRequirement} />
                        ) : null}
                        <div
                          key={activeRequirement.id || `requirement-fields-${reqIndex}`}
                          className={cn(
                            "mx-auto w-full max-w-2xl space-y-4",
                            contactId && "border-t border-bridge-border/30 pt-4",
                          )}
                        >
                          <ContactFieldRow label="Intention" labelWidth="w-[6.5rem]">
                            <MenuSelect
                              hideLabel
                              value={activeRequirement.profileIntention ?? ""}
                              onChange={(v) => {
                                setValue(reqField("profileIntention"), v ?? null, { shouldDirty: true });
                                if (isBuyApplicantIntention(v)) {
                                  setValue(reqField("rentalIntentionFurnishing"), null, { shouldDirty: true });
                                  setValue(reqField("rentalIntentionPriceQualifier"), null, { shouldDirty: true });
                                  setValue(reqField("priceFrom"), null, { shouldDirty: true });
                                  setValue(reqField("priceTo"), null, { shouldDirty: true });
                                  setValue(reqField("bedsMin"), null, { shouldDirty: true });
                                  setValue(reqField("isNewBuild"), null, { shouldDirty: true });
                                } else if (!isRentApplicantIntention(v)) {
                                  setValue(reqField("priceFrom"), null, { shouldDirty: true });
                                  setValue(reqField("priceTo"), null, { shouldDirty: true });
                                  setValue(reqField("bedsMin"), null, { shouldDirty: true });
                                  setValue(reqField("isNewBuild"), null, { shouldDirty: true });
                                  setValue(reqField("rentalIntentionFurnishing"), null, { shouldDirty: true });
                                  setValue(reqField("rentalIntentionPriceQualifier"), null, { shouldDirty: true });
                                }
                              }}
                              options={REQUIREMENT_INTENTION_OPTIONS}
                              placeholder="Select intention"
                            />
                          </ContactFieldRow>
                          <ContactFieldRow label="Furnishing" labelWidth="w-[6.5rem]">
                            <MenuSelect
                              hideLabel
                              value={activeRequirement.rentalIntentionFurnishing ?? ""}
                              onChange={(v) =>
                                setValue(reqField("rentalIntentionFurnishing"), v ?? null, {
                                  shouldDirty: true,
                                })
                              }
                              options={furnishingOptions}
                              placeholder="Select furnishing"
                              disabled={buyIntention}
                            />
                          </ContactFieldRow>
                          <ContactFieldRow label="Qualifier" labelWidth="w-[6.5rem]">
                            <MenuSelect
                              hideLabel
                              value={activeRequirement.rentalIntentionPriceQualifier ?? ""}
                              onChange={(v) =>
                                setValue(reqField("rentalIntentionPriceQualifier"), v ?? null, {
                                  shouldDirty: true,
                                })
                              }
                              options={REQUIREMENT_QUALIFIER_OPTIONS}
                              placeholder="Select qualifier"
                              disabled={buyIntention}
                            />
                          </ContactFieldRow>
                          <ContactFieldRow label="Price from" labelWidth="w-[6.5rem]">
                            <div className="w-full space-y-1">
                              <Input
                                type="number"
                                min={0}
                                step="1"
                                className="w-full"
                                disabled={!hasIntention}
                                {...register(reqField("priceFrom"), {
                                  valueAsNumber: true,
                                  onChange: () => {
                                    void trigger(reqField("priceFrom"));
                                    void trigger(reqField("priceTo"));
                                  },
                                  validate: (v) => {
                                    if (!hasIntention) return true;
                                    if (v === undefined || v === null || Number.isNaN(v)) return true;
                                    if (v < 0 || v > 2147483647) {
                                      return "Price from must be between 0 and 2,147,483,647";
                                    }
                                    return true;
                                  },
                                })}
                              />
                              <FormInlineError
                                message={
                                  errors?.applicantRequirements?.[reqIndex]?.priceFrom?.message as string
                                }
                              />
                            </div>
                          </ContactFieldRow>
                          <ContactFieldRow label="Price to" labelWidth="w-[6.5rem]">
                            <div className="w-full space-y-1">
                              <Input
                                type="number"
                                min={0}
                                step="1"
                                className="w-full"
                                disabled={!hasIntention}
                                {...register(reqField("priceTo"), {
                                  valueAsNumber: true,
                                  onChange: () => {
                                    void trigger(reqField("priceFrom"));
                                    void trigger(reqField("priceTo"));
                                  },
                                  validate: (v) => {
                                    if (!hasIntention) return true;
                                    if (v === undefined || v === null || Number.isNaN(v)) return true;
                                    if (v < 0 || v > 2147483647) {
                                      return "Price to must be between 0 and 2,147,483,647";
                                    }
                                    const priceFrom = activeRequirement.priceFrom;
                                    if (
                                      priceFrom !== undefined &&
                                      priceFrom !== null &&
                                      !Number.isNaN(priceFrom) &&
                                      v < priceFrom
                                    ) {
                                      return "Price to cannot be less than Price from";
                                    }
                                    return true;
                                  },
                                })}
                              />
                              <FormInlineError
                                message={errors?.applicantRequirements?.[reqIndex]?.priceTo?.message as string}
                              />
                            </div>
                          </ContactFieldRow>
                          <ContactFieldRow label="Min beds" labelWidth="w-[6.5rem]">
                            <div className="w-full space-y-1">
                              <Input
                                type="number"
                                min={0}
                                step="1"
                                className="w-full"
                                disabled={!hasIntention}
                                {...register(reqField("bedsMin"), {
                                  valueAsNumber: true,
                                  validate: (v) => {
                                    if (v === undefined || v === null || Number.isNaN(v)) return true;
                                    if (v < 0 || v > 10) return "Min beds must be between 0 and 10";
                                    return true;
                                  },
                                })}
                              />
                              <FormInlineError
                                message={errors?.applicantRequirements?.[reqIndex]?.bedsMin?.message as string}
                              />
                            </div>
                          </ContactFieldRow>
                          <ContactFieldRow label="New build" labelWidth="w-[6.5rem]">
                            <MenuSelect
                              hideLabel
                              value={
                                activeRequirement.isNewBuild == null
                                  ? ""
                                  : activeRequirement.isNewBuild
                                    ? "true"
                                    : "false"
                              }
                              onChange={(v) => {
                                const val = v === "true" ? true : v === "false" ? false : null;
                                setValue(reqField("isNewBuild"), val as boolean | null, { shouldDirty: true });
                              }}
                              options={REQUIREMENT_NEWBUILD_OPTIONS}
                              placeholder="Select"
                              disabled={!hasIntention}
                            />
                          </ContactFieldRow>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-bridge-muted border border-bridge-border/40 rounded-md bg-white">
                {activeTab} module will be loaded here.
              </div>
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
}
function PersonSection({
  title,
  personIndex,
  isPrimary,
  requireOnlySurname = false,
  validatePhoneIfProvided = false,
  secondPersonEnabled = false,
  showEnableToggle = false,
  enabled = true,
  onEnabledChange,
  submitAttempted = false,
  requireContactPoint = false,
  emailInline,
  phoneInline,
  control,
  register,
  watch,
  setValue,
  getValues,
  errors,
}: {
  title: string;
  personIndex: number;
  isPrimary: boolean;
  requireOnlySurname?: boolean;
  /** Enforces contact-phone format for optional phone rows (create flow). */
  validatePhoneIfProvided?: boolean;
  secondPersonEnabled?: boolean;
  showEnableToggle?: boolean;
  enabled?: boolean;
  onEnabledChange?: (enabled: boolean) => void;
  submitAttempted?: boolean;
  /** Updates require each visible person to keep one email and one phone. */
  requireContactPoint?: boolean;
  emailInline?: string;
  phoneInline?: string;
  control: Control<ContactDetails>;
  register: any;
  watch: any;
  setValue: any;
  getValues: UseFormGetValues<ContactDetails>;
  errors: any;
}) {
  const fieldsVisible = !showEnableToggle || enabled;
  const surnameRequired = requireOnlySurname
    ? personIndex === 0 || (personIndex === 1 && secondPersonEnabled)
    : isPrimary;
  const contactFieldsRequired = requireOnlySurname ? false : isPrimary;
  const surnameValue = (watch(`person.${personIndex}.surname`) ?? "").trim();
  const surnameFieldError = errors?.person?.[personIndex]?.surname?.message;
  // Mirrors the save-button tooltip: the person keeps at least one of each, so the
  // reason the save is blocked also sits next to the list it applies to.
  const showEmailRequired =
    requireContactPoint &&
    fieldsVisible &&
    !hasAnyEmailAddress(watch(`person.${personIndex}.emailAddresses`));
  const showPhoneRequired =
    requireContactPoint &&
    fieldsVisible &&
    !hasAnyPhoneNumber(watch(`person.${personIndex}.phoneNumbers`));

  return (
    <div className="space-y-3 rounded-md border border-bridge-border/40 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-medium text-[#009BF5]">{title}</h3>
        {showEnableToggle && onEnabledChange ? (
          <Tooltip
            content={
              enabled
                ? "Turn off to remove the second person. Their name and contact details will be cleared."
                : "Turn on to add a second person to this contact, such as a partner or joint applicant."
            }
            side="left"
          >
            <label className="relative inline-flex shrink-0 cursor-pointer select-none items-center">
              <input
                type="checkbox"
                className="sr-only"
                checked={enabled}
                onChange={(e) => onEnabledChange(e.target.checked)}
                aria-label="Enable second person"
              />
              <span
                className={cn(
                  "block h-6 w-11 rounded-full transition-colors duration-200 ease-in-out",
                  enabled ? "bg-bridge-accent" : "bg-slate-300",
                )}
                aria-hidden
              />
              <span
                className={cn(
                  "absolute left-1 top-1 h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200 ease-in-out",
                  enabled && "translate-x-5",
                )}
                aria-hidden
              />
            </label>
          </Tooltip>
        ) : null}
      </div>
      {fieldsVisible ? (
        <>
      <ContactFieldRow label="Name" required={contactFieldsRequired}>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <div className="w-20 shrink-0">
            <MenuSelect
              hideLabel
              value={watch(`person.${personIndex}.title`) ?? "MR"}
              onChange={(v) =>
                setValue(`person.${personIndex}.title`, v, { shouldDirty: true })
              }
              options={SALUTATION_OPTIONS}
              placeholder="Title"
            />
          </div>
          <Input
            {...register(`person.${personIndex}.forename`, {
              required: contactFieldsRequired ? FORENAME_REQUIRED_MESSAGE : false,
            })}
            placeholder="First name"
            className="min-w-0 flex-1"
          />
        </div>
      </ContactFieldRow>
      <ContactFieldRow label="Surname" required={surnameRequired}>
        <Input
          {...register(`person.${personIndex}.surname`, {
            required: surnameRequired ? SURNAME_REQUIRED_MESSAGE : false,
          })}
          placeholder="Surname"
          className="w-full"
        />
        <FieldValidationError
          show={
            Boolean(surnameFieldError) ||
            (submitAttempted && surnameRequired && !surnameValue)
          }
          message={String(surnameFieldError || SURNAME_REQUIRED_MESSAGE)}
        />
      </ContactFieldRow>
      {/* Inline label — the row now packs every field onto a single compact line. */}
      <ContactFieldRow label="Telephone" required={contactFieldsRequired} alignTop>
        <PhoneNumberFields
          control={control}
          register={register}
          watch={watch}
          setValue={setValue}
          getValues={getValues}
          errors={errors}
          personIndex={personIndex}
          isPrimaryRequired={contactFieldsRequired}
          validateIfProvided={validatePhoneIfProvided}
          inlineMessage={phoneInline}
        />
        {showPhoneRequired ? (
          <div className="mt-1.5">
            <FormInlineError message={PHONE_REQUIRED_FOR_PERSON_MESSAGE} />
          </div>
        ) : null}
      </ContactFieldRow>
      {/* Inline label — matches Telephone; extra addresses live behind the grip toggle. */}
      <ContactFieldRow label="E-Mail" required={contactFieldsRequired} alignTop>
        <EmailAddressFields
          control={control}
          register={register}
          watch={watch}
          setValue={setValue}
          getValues={getValues}
          errors={errors}
          personIndex={personIndex}
          isPrimaryRequired={contactFieldsRequired}
          validateIfProvided={requireOnlySurname}
          inlineMessage={emailInline}
        />
        {showEmailRequired ? (
          <div className="mt-1.5">
            <FormInlineError message={EMAIL_REQUIRED_FOR_PERSON_MESSAGE} />
          </div>
        ) : null}
      </ContactFieldRow>
        </>
      ) : null}
    </div>
  );
}

const EmailAddressRow = ({
  register,
  watch,
  setValue,
  errors,
  personIndex,
  emailIndex,
  isPrimaryRequired = false,
  validateIfProvided = false,
  inlineMessage,
  trailingSlot,
}: {
  register: UseFormRegister<ContactDetails>;
  watch: UseFormWatch<ContactDetails>;
  setValue: UseFormSetValue<ContactDetails>;
  errors: FieldErrors<ContactDetails>;
  personIndex: number;
  emailIndex: number;
  isPrimaryRequired?: boolean;
  validateIfProvided?: boolean;
  inlineMessage?: string;
  /** Slot rendered to the right of the field shell — remove button, grip/plus toggle, or spacer. */
  trailingSlot?: React.ReactNode;
}) => {
  const base = `person.${personIndex}.emailAddresses.${emailIndex}` as const;
  const fieldError = errors?.person?.[personIndex]?.emailAddresses?.[emailIndex]?.emailAddress;
  const emailValue = (watch(`${base}.emailAddress`) ?? "").trim();
  const displayMessage =
    fieldError?.message ??
    (isValidEmailForUniquenessCheck(emailValue) ? inlineMessage : "");
  const emailType = watch(`${base}.type`) ?? "PERSONAL";
  const typeOptions = phoneSelectOptionsWithValue(EMAIL_TYPE_SELECT_OPTIONS, emailType);

  return (
    <div className="w-full min-w-0">
      <div className="flex min-w-0 items-center gap-1.5">
        <div className="flex h-9 min-w-0 flex-1 items-center overflow-clip rounded-md border border-bridge-border/70 bg-bridge-surface/70 transition-colors focus-within:border-bridge-accent/60 focus-within:shadow-ring">
          <div className="w-[96px] shrink-0">
            <MenuSelect
              hideLabel
              value={emailType}
              onChange={(v) => setValue(`${base}.type`, v, { shouldDirty: true })}
              options={typeOptions}
              placeholder="Type"
              triggerClassName={BARE_SELECT_TRIGGER}
            />
          </div>
          <span className="h-4 w-px shrink-0 bg-bridge-border/70" aria-hidden />
          <Input
            type="email"
            {...register(`${base}.emailAddress`, {
              // Only the first row is ever mandatory; extras are opt-in.
              required: isPrimaryRequired ? EMAIL_REQUIRED_MESSAGE : false,
              validate:
                validateIfProvided || emailIndex > 0
                  ? (value: string) => {
                      const trimmed = (value ?? "").trim();
                      if (!trimmed) return true;
                      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed) || "Enter a valid email address";
                    }
                  : undefined,
            })}
            className={cn(BARE_INPUT_SHELL, "min-w-[160px] flex-1 pl-1.5 pr-2")}
            placeholder="Email address"
            autoComplete="email"
          />
        </div>
        {trailingSlot ?? <span className="w-6 shrink-0" aria-hidden />}
      </div>
      {displayMessage ? (
        <div className="mt-1">
          <FormInlineError message={String(displayMessage)} />
        </div>
      ) : null}
    </div>
  );
};

const EmailAddressFields = ({
  control,
  register,
  watch,
  setValue,
  getValues,
  errors,
  personIndex,
  isPrimaryRequired = false,
  validateIfProvided = false,
  inlineMessage,
}: {
  control: Control<ContactDetails>;
  register: UseFormRegister<ContactDetails>;
  watch: UseFormWatch<ContactDetails>;
  setValue: UseFormSetValue<ContactDetails>;
  getValues: UseFormGetValues<ContactDetails>;
  errors: FieldErrors<ContactDetails>;
  personIndex: number;
  isPrimaryRequired?: boolean;
  validateIfProvided?: boolean;
  inlineMessage?: string;
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `person.${personIndex}.emailAddresses` as never,
  });
  // Rows already saved on the backend stay in the form, flagged, until the user saves —
  // hidden here, sent back with `deleted: true` by the update call.
  const rows = fields
    .map((field, emailIndex) => ({ field, emailIndex }))
    .filter(({ emailIndex }) =>
      !isContactPointDeleted(watch(`person.${personIndex}.emailAddresses.${emailIndex}`)),
    );

  const [expanded, setExpanded] = useState(false);
  const belowMax = rows.length < MAX_EMAIL_ADDRESSES_PER_PERSON;
  // Silent no-op on an empty last row — mirrors the phone flow: fill in the current address,
  // then click + to open the next one.
  const lastRow = rows[rows.length - 1];
  const lastAddressFilled = lastRow
    ? Boolean(
        (watch(`person.${personIndex}.emailAddresses.${lastRow.emailIndex}.emailAddress`) ?? "").trim(),
      )
    : false;

  const hasHiddenRowError = rows
    .slice(1)
    .some(({ emailIndex }) => Boolean(errors?.person?.[personIndex]?.emailAddresses?.[emailIndex]));
  useEffect(() => {
    if (hasHiddenRowError) setExpanded(true);
  }, [hasHiddenRowError]);

  // `field.id` is react-hook-form's own key — the record id has to come from the values.
  const handleRemove = (emailIndex: number) => {
    const persistedId = String(
      getValues(`person.${personIndex}.emailAddresses.${emailIndex}.id`) ?? "",
    ).trim();
    if (!persistedId) {
      remove(emailIndex);
      return;
    }
    setValue(`person.${personIndex}.emailAddresses.${emailIndex}.isDeleted`, true, {
      shouldDirty: true,
    });
  };

  const handleAddOrExpand = () => {
    if (!expanded) {
      if (rows.length > 1) {
        setExpanded(true);
        return;
      }
      if (!lastAddressFilled || !belowMax) return;
      append(emptyEmailStub() as never);
      setExpanded(true);
      return;
    }
    if (!lastAddressFilled) return;
    if (!belowMax) return;
    append(emptyEmailStub() as never);
    // Without this the new row lands behind the collapsed state and + flips to the grip.
    setExpanded(true);
  };

  const addDisabled = expanded && !belowMax;
  const toggleTitle = !expanded && rows.length > 1
    ? "Show all email addresses"
    : addDisabled
      ? `Maximum of ${MAX_EMAIL_ADDRESSES_PER_PERSON} email addresses reached`
      : lastAddressFilled
        ? "Add another email address"
        : "Fill in the current address, then click + to add another";

  const visibleRows = expanded ? rows : rows.slice(0, 1);

  return (
    <div className="w-full min-w-0 space-y-1.5">
      {visibleRows.map(({ field, emailIndex }, position) => {
        const isFirstOverall = position === 0;
        const canRemove = rows.length > 1;
        const removeButton = (
          <button
            type="button"
            onClick={() => handleRemove(emailIndex)}
            className="shrink-0 rounded p-1 text-bridge-muted transition-colors hover:bg-red-50 hover:text-red-600"
            aria-label={`Remove email address ${emailIndex + 1}`}
            title="Remove"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        );
        const toggleButton = (
          <Tooltip content={toggleTitle} side="left">
            <button
              type="button"
              onClick={handleAddOrExpand}
              disabled={addDisabled}
              className={cn(
                "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-bridge-muted transition-colors",
                addDisabled
                  ? "cursor-not-allowed text-bridge-muted/50"
                  : "hover:bg-bridge-surface-hover hover:text-bridge-accent",
              )}
              aria-label={
                !expanded && rows.length > 1 ? "Show all email addresses" : "Add another email address"
              }
              aria-expanded={expanded}
            >
              {!expanded && rows.length > 1 ? (
                // Following Alto UI: staircase grip expands hidden email rows; + adds another once expanded.
                <Grip className="h-4 w-4" aria-hidden />
              ) : (
                <Plus className="h-4 w-4" aria-hidden />
              )}
            </button>
          </Tooltip>
        );
        const trailingSlot = isFirstOverall
          ? canRemove
            ? (
                <span className="inline-flex shrink-0 items-center gap-1">
                  {removeButton}
                  {toggleButton}
                </span>
              )
            : toggleButton
          : removeButton;

        return (
          <EmailAddressRow
            key={field.id}
            register={register}
            watch={watch}
            setValue={setValue}
            errors={errors}
            personIndex={personIndex}
            emailIndex={emailIndex}
            isPrimaryRequired={isPrimaryRequired && position === 0}
            validateIfProvided={validateIfProvided}
            inlineMessage={position === 0 ? inlineMessage : undefined}
            trailingSlot={trailingSlot}
          />
        );
      })}
    </div>
  );
};

function FormInlineError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-xs text-bridge-danger">{message}</p>;
}

function FieldValidationError({
  show,
  message,
  className,
}: {
  show: boolean;
  message: string;
  className?: string;
}) {
  if (!show) return null;
  return (
    <div
      className={`mt-1 flex animate-in items-center gap-1.5 text-xs font-medium text-red-500 fade-in slide-in-from-top-1 duration-200${className ? ` ${className}` : ""}`}
    >
      <AlertCircle className="h-3.5 w-3.5 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

const CountryCodeSelect = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) => {
  const { countryCodeOptions, loading } = useCountries();
  return (
    <MenuSelect
      value={value}
      onChange={onChange}
      options={countryCodeOptions}
      placeholder="Select country"
      loading={loading}
      searchable
    />
  );
};

/** One editable phone row. `inlineMessage` only applies to the first row. */
/** Strips a control's own border/height so it sits flush inside the shared phone-row shell. */
const BARE_SELECT_TRIGGER =
  "min-h-0 h-8 rounded-none border-0 bg-transparent px-1.5 text-xs shadow-none ring-0";
/** Tighter variant for the dial-code select where every pixel matters. */
const BARE_CODE_SELECT_TRIGGER =
  "min-h-0 h-8 rounded-none border-0 bg-transparent pl-1.5 pr-0.5 gap-1 text-xs shadow-none ring-0";
const BARE_INPUT_SHELL =
  "h-8 rounded-none border-0 bg-transparent px-1.5 text-xs shadow-none focus-within:border-0 focus-within:shadow-none";

const PhoneNumberRow = ({
  register,
  watch,
  setValue,
  errors,
  personIndex,
  phoneIndex,
  isPrimaryRequired = false,
  validateIfProvided = false,
  inlineMessage,
  trailingSlot,
  phoneCodeOptions,
  loadingPhoneCodes,
}: {
  register: UseFormRegister<ContactDetails>;
  watch: UseFormWatch<ContactDetails>;
  setValue: UseFormSetValue<ContactDetails>;
  errors: FieldErrors<ContactDetails>;
  personIndex: number;
  phoneIndex: number;
  isPrimaryRequired?: boolean;
  validateIfProvided?: boolean;
  inlineMessage?: string;
  /** Slot rendered to the right of the field shell — remove button, expand toggle, or spacer. */
  trailingSlot?: React.ReactNode;
  phoneCodeOptions: { value: string; label: string }[];
  loadingPhoneCodes: boolean;
}) => {
  const base = `person.${personIndex}.phoneNumbers.${phoneIndex}` as const;
  const fieldError = errors?.person?.[personIndex]?.phoneNumbers?.[phoneIndex]?.number;
  const phoneValue = (watch(`${base}.number`) ?? "").trim();
  const displayMessage =
    fieldError?.message ??
    (isValidPhoneForUniquenessCheck(phoneValue) ? inlineMessage : "");
  const phoneType = watch(`${base}.type`) ?? "HOME";
  const typeOptions = phoneSelectOptionsWithValue(PHONE_TYPE_SELECT_OPTIONS, phoneType);
  const phoneCodeField = `${base}.countryDialCode` as const;
  const rawPhoneCode = watch(phoneCodeField) ?? "";
  const formCountryCode = watch("countryCode") ?? "";
  const currentPhoneCode = resolvePhoneCodeOptionValue(rawPhoneCode, formCountryCode, phoneCodeOptions);

  return (
    <div className="w-full min-w-0">
      <div className="flex min-w-0 items-center gap-1.5">
        {/* One shell around every field so the row reads as a single control instead of four. */}
        {/* overflow-clip, not hidden: a `hidden` container still scrolls when a child is focused, which
            would slide the type select out of view as soon as you tab into the label. */}
        <div className="flex h-9 min-w-0 flex-1 items-center overflow-clip rounded-md border border-bridge-border/70 bg-bridge-surface/70 transition-colors focus-within:border-bridge-accent/60 focus-within:shadow-ring">
          <div className="w-[68px] shrink-0">
            <MenuSelect
              hideLabel
              value={phoneType}
              onChange={(v) => setValue(`${base}.type`, v, { shouldDirty: true })}
              options={typeOptions}
              placeholder="Type"
              triggerClassName={BARE_SELECT_TRIGGER}
            />
          </div>
          <span className="h-4 w-px shrink-0 bg-bridge-border/70" aria-hidden />
          {/* Code trigger only needs room for the dial code itself now that country lives in the subtitle. */}
          <div className="w-[60px] shrink-0">
            <MenuSelect
              hideLabel
              value={currentPhoneCode}
              onChange={(v) => {
                const [dialCode, country] = v.split("|");
                setValue(phoneCodeField, dialCode, { shouldDirty: true });
                if (personIndex === 0 && phoneIndex === 0 && country) {
                  setValue("countryCode", country, { shouldDirty: true });
                }
              }}
              options={phoneCodeOptions}
              placeholder="+…"
              loading={loadingPhoneCodes}
              searchable
              triggerClassName={BARE_CODE_SELECT_TRIGGER}
            />
          </div>
          {/* The number holds a hard floor and the label yields first, so the number can never be squeezed to nothing. */}
          <Input
            {...register(`${base}.number`, {
              required: isPrimaryRequired ? PHONE_REQUIRED_MESSAGE : false,
              validate:
                validateIfProvided
                  ? (value: string) => {
                      const trimmed = (value ?? "").trim();
                      return (
                        !trimmed ||
                        CONTACT_MOBILE_PHONE_REGEX.test(trimmed) ||
                        CONTACT_MOBILE_PHONE_VALIDATION_MESSAGE
                      );
                    }
                  : undefined,
            })}
            className={cn(BARE_INPUT_SHELL, "min-w-[104px] flex-[2_1_0%] pl-1.5")}
            type="text"
            inputMode="text"
            autoComplete="tel"
            placeholder="Number"
          />
          <span className="h-4 w-px shrink-0 bg-bridge-border/70" aria-hidden />
          <Input
            {...register(`${base}.description`)}
            className={cn(BARE_INPUT_SHELL, "min-w-[80px] flex-[1_1_0%] pr-2")}
            placeholder="Label"
          />
        </div>
        {trailingSlot ?? <span className="w-6 shrink-0" aria-hidden />}
      </div>
      {displayMessage ? (
        <div className="mt-1">
          <FormInlineError message={String(displayMessage)} />
        </div>
      ) : null}
    </div>
  );
};

const PhoneNumberFields = ({
  control,
  register,
  watch,
  setValue,
  getValues,
  errors,
  personIndex,
  isPrimaryRequired = false,
  validateIfProvided = false,
  inlineMessage,
}: {
  control: Control<ContactDetails>;
  register: UseFormRegister<ContactDetails>;
  watch: UseFormWatch<ContactDetails>;
  setValue: UseFormSetValue<ContactDetails>;
  getValues: UseFormGetValues<ContactDetails>;
  errors: FieldErrors<ContactDetails>;
  personIndex: number;
  isPrimaryRequired?: boolean;
  validateIfProvided?: boolean;
  inlineMessage?: string;
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `person.${personIndex}.phoneNumbers` as never,
  });
  const { phoneCodeOptions, loading: loadingPhoneCodes } = useCountries();
  // Saved rows are flagged rather than removed — see EmailAddressFields.
  const rows = fields
    .map((field, phoneIndex) => ({ field, phoneIndex }))
    .filter(({ phoneIndex }) =>
      !isContactPointDeleted(watch(`person.${personIndex}.phoneNumbers.${phoneIndex}`)),
    );

  // Collapsed by default: only the first phone stays on-screen; the rest live behind the grip.
  const [expanded, setExpanded] = useState(false);
  const belowMax = rows.length < MAX_PHONE_NUMBERS_PER_PERSON;
  // The plus button silently no-ops when the newest row is still empty — the requested flow is
  // "fill the number, then click + to open the next row"; hammering + on an empty row does nothing.
  const lastRow = rows[rows.length - 1];
  const lastNumberFilled = lastRow
    ? Boolean((watch(`person.${personIndex}.phoneNumbers.${lastRow.phoneIndex}.number`) ?? "").trim())
    : false;

  // Safety net: if a hidden row picks up an error (validation, uniqueness), pop the section open
  // so the user can actually see and fix it.
  const hasHiddenRowError = rows
    .slice(1)
    .some(({ phoneIndex }) => Boolean(errors?.person?.[personIndex]?.phoneNumbers?.[phoneIndex]));
  useEffect(() => {
    if (hasHiddenRowError) setExpanded(true);
  }, [hasHiddenRowError]);

  // `field.id` is react-hook-form's own key — the record id has to come from the values.
  const handleRemove = (phoneIndex: number) => {
    const persistedId = String(
      getValues(`person.${personIndex}.phoneNumbers.${phoneIndex}.id`) ?? "",
    ).trim();
    if (!persistedId) {
      remove(phoneIndex);
      return;
    }
    setValue(`person.${personIndex}.phoneNumbers.${phoneIndex}.isDeleted`, true, {
      shouldDirty: true,
    });
  };

  const handleAddOrExpand = () => {
    if (!expanded) {
      if (rows.length > 1) {
        setExpanded(true);
        return;
      }
      if (!lastNumberFilled || !belowMax) return;
      append({
        ...emptyPhoneStub(),
        // Additional numbers almost always share the first row's country.
        countryDialCode:
          watch(`person.${personIndex}.phoneNumbers.0.countryDialCode`) ||
          emptyPhoneStub().countryDialCode,
      } as never);
      setExpanded(true);
      return;
    }
    // Second-click guard: don't stack empty rows if the user is spam-clicking + before typing.
    if (!lastNumberFilled) return;
    if (!belowMax) return;
    append({
      ...emptyPhoneStub(),
      // Additional numbers almost always share the first row's country.
      countryDialCode:
        watch(`person.${personIndex}.phoneNumbers.0.countryDialCode`) ||
        emptyPhoneStub().countryDialCode,
    } as never);
  };

  const visibleRows = expanded ? rows : rows.slice(0, 1);
  const addDisabled = expanded && !belowMax;
  const toggleTitle = !expanded && rows.length > 1
    ? "Show all phone numbers"
    : addDisabled
      ? `Maximum of ${MAX_PHONE_NUMBERS_PER_PERSON} phone numbers reached`
      : lastNumberFilled
        ? "Add another phone number"
        : "Fill in the current number, then click + to add another";

  return (
    <div className="w-full min-w-0 space-y-1.5">
      {visibleRows.map(({ field, phoneIndex }, position) => {
        const isFirstOverall = position === 0;
        const canRemove = rows.length > 1;
        const removeButton = (
          <button
            type="button"
            onClick={() => handleRemove(phoneIndex)}
            className="shrink-0 rounded p-1 text-bridge-muted transition-colors hover:bg-red-50 hover:text-red-600"
            aria-label={`Remove phone number ${phoneIndex + 1}`}
            title="Remove"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        );
        const toggleButton = (
          <Tooltip content={toggleTitle} side="left">
            <button
              type="button"
              onClick={handleAddOrExpand}
              disabled={addDisabled}
              className={cn(
                "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-bridge-muted transition-colors",
                addDisabled
                  ? "cursor-not-allowed text-bridge-muted/50"
                  : "hover:bg-bridge-surface-hover hover:text-bridge-accent",
              )}
              aria-label={
                !expanded && rows.length > 1 ? "Show all phone numbers" : "Add another phone number"
              }
              aria-expanded={expanded}
            >
              {!expanded && rows.length > 1 ? (
                // Following Alto UI: staircase grip expands hidden phone rows; + adds another once expanded.
                <Grip className="h-4 w-4" aria-hidden />
              ) : (
                <Plus className="h-4 w-4" aria-hidden />
              )}
            </button>
          </Tooltip>
        );
        const trailingSlot = isFirstOverall
          ? canRemove
            ? (
                <span className="inline-flex shrink-0 items-center gap-1">
                  {removeButton}
                  {toggleButton}
                </span>
              )
            : toggleButton
          : removeButton;

        return (
          <PhoneNumberRow
            key={field.id}
            register={register}
            watch={watch}
            setValue={setValue}
            errors={errors}
            personIndex={personIndex}
            phoneIndex={phoneIndex}
            isPrimaryRequired={isPrimaryRequired && position === 0}
            validateIfProvided={validateIfProvided}
            inlineMessage={position === 0 ? inlineMessage : undefined}
            trailingSlot={trailingSlot}
            phoneCodeOptions={phoneCodeOptions}
            loadingPhoneCodes={loadingPhoneCodes}
          />
        );
      })}
    </div>
  );
};
function RequiredMark() {
  return <span className="text-red-500">*</span>;
}

/** Fades and blocks interaction for fields that cannot be edited on existing contacts. */
function EditLocked({
  locked = true,
  children,
  className,
}: {
  locked?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  if (!locked) return <>{children}</>;
  return (
    <div className={cn("pointer-events-none select-none opacity-50", className)} aria-disabled="true">
      {children}
    </div>
  );
}

function ContactFormSection({
  title,
  children,
}: {
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3 border-t border-bridge-border/40 pt-4 first:border-t-0 first:pt-0">
      {title ? <h3 className="text-sm font-medium text-[#009BF5]">{title}</h3> : null}
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function ContactFormRow({
  children,
  align = "center",
}: {
  children: React.ReactNode;
  align?: "center" | "start";
}) {
  return (
    <div
      className={
        align === "start"
          ? "grid grid-cols-1 items-start gap-4 lg:grid-cols-2 lg:gap-x-8"
          : "grid grid-cols-1 items-center gap-4 lg:grid-cols-2 lg:gap-x-8"
      }
    >
      {children}
    </div>
  );
}

/** Label + control row used in person blocks and salutation (matches reference Contact UI). */
function ContactFieldRow({
  label,
  children,
  required = false,
  alignTop = false,
  labelWidth = "w-[5.5rem]",
  className = "",
  stacked = false,
}: {
  label: string;
  children: React.ReactNode;
  required?: boolean;
  alignTop?: boolean;
  labelWidth?: string;
  className?: string;
  /** Puts the label on its own line so the control gets the full drawer width. */
  stacked?: boolean;
}) {
  const labelContent = (
    <>
      {label}
      {required ? (
        <>
          {" "}
          <RequiredMark />
        </>
      ) : null}
    </>
  );

  if (stacked) {
    return (
      <div className={cn("min-w-0", className)}>
        <label className="mb-1.5 block text-sm text-bridge-muted">{labelContent}</label>
        <div className="min-w-0">{children}</div>
      </div>
    );
  }

  return (
    <div
      className={`flex min-w-0 gap-2 ${alignTop ? "items-start" : "items-center"} ${className}`}
    >
      <label
        className={`${labelWidth} shrink-0 text-sm text-bridge-muted ${
          alignTop ? "pt-2.5" : ""
        }`}
      >
        {labelContent}
      </label>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

function Field({
  label,
  children,
  required = false,
  multiline = false,
  className = "",
}: {
  label: string;
  children: React.ReactNode;
  required?: boolean;
  multiline?: boolean;
  className?: string;
}) {
  return (
    <ContactFieldRow
      label={label}
      required={required}
      alignTop={multiline}
      labelWidth="w-[7rem]"
      className={className}
    >
      {children}
    </ContactFieldRow>
  );
}

function contactFieldDisplay(
  formValue: string | null | undefined,
  apiValue: string | null | undefined,
): string {
  const v = (formValue ?? apiValue)?.trim();
  return v || "-";
}

function ReadOnlyValue({ value, fallback = "-" }: { value?: string | null; fallback?: string }) {
  return (
    <div className="text-sm font-medium text-bridge-fg px-3 py-2 bg-gray-50/50 rounded-md border border-bridge-border/40 min-h-[38px] flex items-center shadow-sm">
      {value || <span className="text-bridge-muted font-normal">{fallback}</span>}
    </div>
  );
}
